"use client";

import { Article } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import type { ReadingSlide } from "../../../../lib/cms-server";
import type { Tone } from "../../../../lib/home-mock";
import { TONE_BG, TONE_INK } from "./tones";

// «خواندنی‌ها»: one post per view, edge to edge — the card *is* the carousel
// (no padding, no header row), each slide fills it and takes its color from
// `tones` in turn (home passes one fixed color, F143; the blog a shuffle). CSS scroll-snap does the swiping (touch,
// trackpad, shift+wheel). The dots sit on top of the slides; they scroll by
// measured offsets rather than scrollLeft math, since scrollLeft is negative
// in RTL. `withExcerpt` (home) adds the post's summary under its title, two
// lines on phones and three from sm up.
export function ReadingsCarousel({ slides, tones, withExcerpt = false }: { slides: ReadingSlide[]; tones: Tone[]; withExcerpt?: boolean }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      { root: track, threshold: 0.6 },
    );
    for (const el of slideRefs.current) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [slides.length]);

  const goTo = (index: number) => {
    const track = trackRef.current;
    const target = slideRefs.current[index];
    if (!track || !target) return;
    // RTL: a slide's "start" is its right edge.
    const delta = target.getBoundingClientRect().right - track.getBoundingClientRect().right;
    track.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div className="relative h-full min-h-52 overflow-hidden rounded-card shadow-sm">
      <div
        ref={trackRef}
        className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide, index) => {
          const tone = tones[index % tones.length]!;
          return (
            <Link
              key={slide.id}
              href={slide.href}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              data-index={index}
              className={cn("flex shrink-0 basis-full snap-start flex-col justify-between gap-6 p-5 pb-10 sm:p-6 sm:pb-11", TONE_BG[tone])}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={cn("flex items-center gap-1.5 text-sm font-bold", TONE_INK[tone])}>
                  <Article size={18} />
                  خواندنی‌ها
                </span>
                <span className="bg-background/70 rounded-full px-2.5 py-1 text-xs font-medium">{slide.category}</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <h3 className="text-lg leading-8 font-bold sm:text-xl">{slide.title}</h3>
                {withExcerpt && slide.excerpt ? (
                  <p className="text-foreground/75 line-clamp-2 max-w-[60ch] text-sm leading-6 sm:line-clamp-3">{slide.excerpt}</p>
                ) : null}
                <span className={cn("text-xs", TONE_INK[tone])}>
                  {slide.readMinutes.toLocaleString("fa-IR")} دقیقه مطالعه
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5" role="tablist" aria-label="اسلایدها">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-label={`اسلاید ${(index + 1).toLocaleString("fa-IR")}`}
            onClick={() => goTo(index)}
            className={cn(
              "h-1.5 rounded-full transition-all",
              index === active ? "bg-foreground w-5" : "bg-foreground/20 w-1.5",
            )}
          />
        ))}
      </div>
    </div>
  );
}
